// ConflictResolution.tsx
import React, { useState, useMemo } from 'react';
import { AlertTriangle, ArrowRight, ArrowDown, ArrowUp, ArrowUpDown, Check, X, Info, Filter } from 'lucide-react';
import { 
  ConflictAnalysisResult, 
  DataConflict, 
  formatConflictValue, 
  getConflictSeverityColor,
  getConflictDescription 
} from '../../lib/data/conflictAnalysis';

interface ConflictResolutionProps {
  conflictAnalysis: ConflictAnalysisResult;
  onResolve: (resolutions: { [key: string]: 'keep_existing' | 'use_new' }) => void;
  onCancel: () => void;
}

type FilterType = 'all' | 'value_change' | 'new_account' | 'new_period';
type SortField = 'accountNumber' | 'accountName' | 'month' | 'percentChange' | 'conflictType';
type SortDirection = 'asc' | 'desc';

export function ConflictResolution({ conflictAnalysis, onResolve, onCancel }: ConflictResolutionProps) {
  const [resolutions, setResolutions] = useState<{ [key: string]: 'keep_existing' | 'use_new' }>({});
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [sortField, setSortField] = useState<SortField>('accountNumber');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [bulkAction, setBulkAction] = useState<'keep_all' | 'use_all' | null>(null);

  // Filter and sort conflicts
  const filteredAndSortedConflicts = useMemo(() => {
    let filtered = conflictAnalysis.conflicts;

    // Apply filter
    if (filterType !== 'all') {
      filtered = filtered.filter(conflict => conflict.conflictType === filterType);
    }

    // Apply sort
    filtered.sort((a, b) => {
      let aValue: string | number | undefined = a[sortField];
      let bValue: string | number | undefined = b[sortField];

      // Handle percentChange special case
      if (sortField === 'percentChange') {
        aValue = Math.abs(a.percentChange);
        bValue = Math.abs(b.percentChange);
      }

      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === undefined) return sortDirection === 'asc' ? -1 : 1;

      // Convert strings to lowercase for comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle mixed types by converting to strings
      if (typeof aValue !== typeof bValue) {
        aValue = String(aValue);
        bValue = String(bValue);
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [conflictAnalysis.conflicts, filterType, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleResolutionChange = (conflictKey: string, resolution: 'keep_existing' | 'use_new') => {
    setResolutions(prev => ({
      ...prev,
      [conflictKey]: resolution
    }));
  };

  const handleBulkAction = (action: 'keep_all' | 'use_all') => {
    const newResolutions: { [key: string]: 'keep_existing' | 'use_new' } = {};
    filteredAndSortedConflicts.forEach(conflict => {
      const key = `${conflict.accountNumber}-${conflict.month}`;
      newResolutions[key] = action === 'keep_all' ? 'keep_existing' : 'use_new';
    });
    setResolutions(prev => ({ ...prev, ...newResolutions }));
    setBulkAction(action);
  };

  const handleProceed = () => {
    // Apply default resolution (use_new) for any unresolved conflicts
    const finalResolutions = { ...resolutions };
    conflictAnalysis.conflicts.forEach(conflict => {
      const key = `${conflict.accountNumber}-${conflict.month}`;
      if (!finalResolutions[key]) {
        finalResolutions[key] = 'use_new';
      }
    });
    onResolve(finalResolutions);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" />
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  const conflictTypeCounts = {
    all: conflictAnalysis.conflicts.length,
    value_change: conflictAnalysis.conflicts.filter(c => c.conflictType === 'value_change').length,
    new_account: conflictAnalysis.conflicts.filter(c => c.conflictType === 'new_account').length,
    new_period: conflictAnalysis.conflicts.filter(c => c.conflictType === 'new_period').length,
  };

  return (
    <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="bg-orange-50 border-b border-orange-200 rounded-t-lg p-6">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-orange-600" />
          <div>
            <h2 className="text-xl font-semibold text-orange-900">
              Data Conflicts Detected
            </h2>
            <p className="text-sm text-orange-700 mt-1">
              Review the conflicts below and choose how to resolve them before proceeding.
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">{conflictAnalysis.totalConflicts}</div>
            <div className="text-sm text-blue-600">Total Conflicts</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{conflictAnalysis.summary.accountsAffected}</div>
            <div className="text-sm text-green-600">Accounts Affected</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-600">{conflictAnalysis.summary.periodsAffected}</div>
            <div className="text-sm text-purple-600">Periods Affected</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">{conflictAnalysis.summary.significantChanges}</div>
            <div className="text-sm text-red-600">Significant Changes</div>
          </div>
        </div>

        {/* New Data Summary */}
        {(conflictAnalysis.newAccounts.length > 0 || conflictAnalysis.newPeriods.length > 0) && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-blue-900">New Data Summary</span>
            </div>
            <div className="text-sm text-blue-700 space-y-1">
              {conflictAnalysis.newAccounts.length > 0 && (
                <div>• {conflictAnalysis.newAccounts.length} new accounts will be added</div>
              )}
              {conflictAnalysis.newPeriods.length > 0 && (
                <div>• {conflictAnalysis.newPeriods.length} new periods: {conflictAnalysis.newPeriods.join(', ')}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Filters and Bulk Actions */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* Filters */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700">Filter:</span>
            {(['all', 'value_change', 'new_account', 'new_period'] as FilterType[]).map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  filterType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {type === 'all' ? 'All' : 
                 type === 'value_change' ? 'Value Changes' :
                 type === 'new_account' ? 'New Accounts' : 
                 'New Periods'} ({conflictTypeCounts[type]})
              </button>
            ))}
          </div>

          {/* Bulk Actions */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Bulk:</span>
            <button
              onClick={() => handleBulkAction('use_all')}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Use All New
            </button>
            <button
              onClick={() => handleBulkAction('keep_all')}
              className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Keep All Existing
            </button>
          </div>
        </div>
      </div>

      {/* Conflicts Table */}
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={() => handleSort('accountNumber')}
                  className="flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-blue-600"
                >
                  Account
                  {getSortIcon('accountNumber')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={() => handleSort('month')}
                  className="flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-blue-600"
                >
                  Period
                  {getSortIcon('month')}
                </button>
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">Existing</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">→</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">New</th>
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={() => handleSort('conflictType')}
                  className="flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-blue-600"
                >
                  Change
                  {getSortIcon('conflictType')}
                </button>
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">Resolution</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredAndSortedConflicts.map((conflict) => {
              const key = `${conflict.accountNumber}-${conflict.month}`;
              const resolution = resolutions[key] || 'use_new';
              const severityColor = getConflictSeverityColor(conflict);
              
              return (
                <tr key={key} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{conflict.accountNumber}</div>
                      <div className="text-gray-500 truncate max-w-40">{conflict.accountName}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{conflict.month}</td>
                  <td className="px-4 py-3 text-center text-sm">
                    {conflict.conflictType === 'new_account' || conflict.conflictType === 'new_period' 
                      ? <span className="text-gray-400">—</span>
                      : formatConflictValue(conflict.existingValue)
                    }
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ArrowRight className={`w-4 h-4 mx-auto ${severityColor}`} />
                  </td>
                  <td className="px-4 py-3 text-center text-sm font-medium">
                    {formatConflictValue(conflict.newValue)}
                  </td>
                  <td className="px-4 py-3">
                    <div className={`text-sm ${severityColor}`}>
                      {getConflictDescription(conflict)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => handleResolutionChange(key, 'keep_existing')}
                        disabled={conflict.conflictType === 'new_account' || conflict.conflictType === 'new_period'}
                        className={`p-1 rounded transition-colors ${
                          resolution === 'keep_existing'
                            ? 'bg-gray-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        } disabled:opacity-30 disabled:cursor-not-allowed`}
                        title="Keep existing data"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleResolutionChange(key, 'use_new')}
                        className={`p-1 rounded transition-colors ${
                          resolution === 'use_new'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                        title="Use new data"
                      >
                        <Check className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="p-6 bg-gray-50 rounded-b-lg border-t border-gray-200">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {filteredAndSortedConflicts.length} conflicts shown • 
            Categories will be preserved from existing data
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel Upload
            </button>
            <button
              onClick={handleProceed}
              className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Apply Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}